import React, { useState, useEffect } from 'react';

//Interface
import { IAppProps } from '@interfaces/App.interface';

import { Grid } from '@ts/storybook';

//Components
import { GridBreak } from '@components/Utils';
import { KPICard, AreaChart, BarChart } from '@components/KPI';

import { getDistinct, sortByAttribute, generateDateArray } from './DataBase/utils';
import { IDataChart } from '@ts/ui-models';

//Redux
import { useAppDispatch } from '@redux/store';
import { fetchData } from '@redux/slices/functionals/request/requestActions';
import { height } from '@mui/system';
import { useTranslation } from 'react-i18next';

interface DashboardProp extends IAppProps {}

type getDataType = (() => Promise<any>) | ((slot: string) => Promise<any>);

interface ListChart {
  id: number;
  title: string;
  slot?: string;
  data: IDataChart | undefined;
  getData: getDataType;
  loading: boolean;
}

const DashboardPage: React.FC<DashboardProp> = props => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const lng = i18n.language;
  const [slot, setSlot] = useState<{ id: number | undefined; value: string }>({ id: undefined, value: 'week' });

  const [renderedCards, setRenderedCards] = useState<any[]>([
    {
      id: 0,
      title: 'Activity last Hour',
      data: undefined,
      getData: () => getDataForCardActivityLastHour(),
      loading: true,
    },
    {
      id: 1,
      title: 'Events today',
      data: undefined,
      getData: () => getDataForCardEventsToday(),
      loading: true,
    },
    {
      id: 2,
      title: 'Today notifications',
      data: undefined,
      getData: () => getDataForCardNotifications(),
      loading: true,
    },
  ]);

  const [renderedBarCharts, setRenderedBarCharts] = useState<ListChart[]>([
    {
      id: 0,
      title: 'Top Locations',
      data: undefined,
      getData: () => getDataForChartTopLocations(),
      loading: true,
    },
    {
      id: 1,
      title: 'Items Types',
      data: undefined,
      getData: () => getDataForChartItemTypes(),
      loading: true,
    },
    {
      id: 2,
      title: 'Labels',
      data: undefined,
      getData: () => getDataForChartItemLabels(),
      loading: true,
    },
  ]);

  /*   const [renderedPieCharts, setRenderedPieCharts] = useState<ListChart[]>([
    {
      id: 0,
      title: 'Warnings by severity',
      data: undefined,
      getData: () => getDataForChartWarningBySeverity(),
      loading: true,
    },
  ]); */

  const [renderedAreaCharts, setRenderedAreaCharts] = useState<ListChart[]>([
    {
      id: 0,
      title: 'Top Events',
      slot: 'week',
      data: undefined,
      getData: (slot: string) => getDataForChartEventsTime(slot),
      loading: true,
    },
    {
      id: 1,
      title: 'Evolution of data activity',
      slot: 'week',
      data: undefined,
      getData: (slot: string) => getDataForChartReadingsTime(slot),
      loading: true,
    },
  ]);

  //#region Handlers
  const onChangeSlot = (item: any, type: string) => {
    setRenderedAreaCharts(prevItems => {
      return prevItems.map(area => {
        if (area.id === item.id) {
          return { ...area, slot: type, getData: () => area.getData(type) };
        }
        return area;
      });
    });
    setSlot({ id: item.id, value: type });
  };
  //#endregion

  //#region Functions
  const updateData = async (list: any[], saveFunction: any, idUpdate?: number) => {
    /* saveFunction(
      list.map(async chart => {
        if (idUpdate === undefined || idUpdate === chart.id) {
          return { ...chart, loading: true };
        }
        return chart;
      })
    ); */

    const updatedCards = await Promise.all(
      list.map(async chart => {
        if (idUpdate === undefined || idUpdate === chart.id) {
          const data = await chart.getData(slot.value);

          return { ...chart, data, loading: false };
        }
        return chart;
      })
    );
    saveFunction(updatedCards);
  };

  const generateArrayEmpty = (value: string): any => {
    if (value === 'week') {
      return [{ name: 'No data', data: Array(7).fill(0) }];
    } else if (value === 'month') {
      return [{ name: 'No data', data: Array(12).fill(0) }];
    } else {
      throw new Error('Invalid value. Must be either "week" or "month".');
    }
  };
  const checkArrayAllZeros = (array: number[]) => {
    if (array) {
      for (const element of array) {
        if (element !== 0) {
          return false;
        }
      }
      return true;
    }
    return false;
  };
  const getData = (series: any) => {
    // Verificar si series es una lista de objetos
    if (Array.isArray(series) && typeof series[0] === 'number') {
      return series;
    } else if (Array.isArray(series) && typeof series[0] === 'object') {
      return series[0].data;
    }
  };
  const checkEmptyData = (data: IDataChart) => {
    if (checkArrayAllZeros(getData(data?.series))) return true;
    return !data || data?.series.length === 0;
  };
  //KPI
  const getDataForCardActivityLastHour = async () => {
    let needs = [
      {
        url: '/api/v2/readings',
        data: {
          where: {
            timestamp: {
              $gte: new Date(new Date().setHours(new Date().getHours() - 1)).toISOString(), //one hour less
              $lte: new Date().toISOString(),
            },
          },
          findAndCount: true,
          limit: 1,
          offset: 0,
        },
      },
      {
        url: '/api/v2/readings',
        data: {
          where: {
            timestamp: {
              $gte: new Date(new Date().setHours(new Date().getHours() - 2)).toISOString(), //two hour less
              $lte: new Date(new Date().setHours(new Date().getHours() - 1)).toISOString(), //one hour less
            },
          },
          findAndCount: true,
          limit: 1,
          offset: 0,
        },
      },
    ];

    const [actLastHour, yesterdayActLastHour] = await Promise.all(
      needs.map(needs => dispatch(fetchData({ url: needs.url, dataBody: needs.data, withoutCancel: true })))
    );

    return { oldValue: yesterdayActLastHour.payload?.data?.length ?? 0, value: actLastHour.payload?.data?.length ?? 0 };
  };
  const getDataForCardEventsToday = async () => {
    let needs = [
      {
        url: '/api/v2/events',
        data: {
          where: {
            timestamp: {
              $gte: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
              $lte: new Date().toISOString(),
            },
          },
          findAndCount: true,
          limit: 1,
          offset: 0,
        },
      },
      {
        url: '/api/v2/events',
        data: {
          where: {
            timestamp: {
              $gte: new Date(new Date(new Date().setDate(new Date().getDate() - 2))).toISOString(),
              $lte: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
            },
          },
          findAndCount: true,
          limit: 1,
          offset: 0,
        },
      },
    ];

    const [eventsToday, yesterdayEvents] = await Promise.all(
      needs.map(needs => dispatch(fetchData({ url: needs.url, dataBody: needs.data, withoutCancel: true })))
    );

    return { oldValue: yesterdayEvents.payload?.count ?? 0, value: eventsToday.payload?.count ?? 0 };
  };
  const getDataForCardNotifications = async () => {
    let needs = [
      {
        url: '/api/notifications',
        dataPath: 'cardNotifications',
        data: {
          where: {
            updatedAt: {
              $gte: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
              $lte: new Date().toISOString(),
            },
          },
          findAndCount: true,
          limit: 1,
          offset: 0,
        },
      },
      {
        url: '/api/notifications',
        dataPath: 'cardNotificationsLastDay',
        data: {
          where: {
            updatedAt: {
              $gte: new Date(new Date(new Date().setDate(new Date().getDate() - 2))).toISOString(),
              $lte: new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
            },
          },
          findAndCount: true,
          limit: 1,
          offset: 0,
        },
      },
    ];

    const [notifToday, yesterdayNotif] = await Promise.all(
      needs.map(needs => dispatch(fetchData({ url: needs.url, dataBody: needs.data, withoutCancel: true })))
    );
    return { oldValue: yesterdayNotif.payload?.count ?? 0, value: notifToday.payload?.count ?? 0 };
  };

  //BARCHART
  const getDataForChartTopLocations = async () => {
    let listLocations: any[] = [];
    let reqListLocations = {
      url: '/api/v2/items',
      data: {
        filters: { location: { isAlive: true } },
        findAndCount: true,
      },
    };
    const data = (await dispatch(fetchData({ url: reqListLocations.url, dataBody: reqListLocations.data, withoutCancel: true })))
      .payload?.data;
    if (data?.rows) {
      listLocations = getDistinct(data.rows, ['logicalId'], 'nItemsInside');
    }

    listLocations = sortByAttribute(listLocations, 'count');
    return {
      xaxis: {
        categories: listLocations.map(l => l.name),
      },
      series: [
        {
          name: 'Location',
          data: listLocations.map(l => l.count),
        },
      ],
    };
  };
  const getDataForChartItemTypes = async () => {
    const listFlavors = [
      { name: 'asset', label: 'Asset' },
      { name: 'itemInCustody', label: 'Items in custody' },
      { name: 'person', label: 'Person' },
      { name: 'process', label: 'Process' },
      { name: 'vehicle', label: 'Vehicle' },
      { name: 'wipPart', label: 'Wip part' },
    ];

    let reqItemTypes = {
      url: '/api/itemTypes',
      dataPath: 'listItemType',
      data: {
        findAndCount: true,
        where: { isAlive: true },
      },
    };
    const data = (await dispatch(fetchData({ url: reqItemTypes.url, dataBody: reqItemTypes.data, withoutCancel: true }))).payload
      ?.data;
    let listLogicalId: any[] = [];
    if (data?.rows) {
      data.rows.forEach((itemType: any) => {
        if (listFlavors.map(f => f.name).includes(itemType.typeParams.mainFlavor)) {
          listLogicalId.push({
            logicalId: itemType.logicalId,
            name: itemType.name,
            flavor: itemType.typeParams.mainFlavor,
            count: 0,
          });
        }
      });
    }

    let needs: any[] = [];

    listLogicalId.forEach(item => {
      needs.push({
        url: '/api/v2/items',
        data: {
          where: { elementTypeId: item.logicalId, isAlive: true },
          findAndCount: true,
          limit: 1,
          offset: 0,
        },
      });
    });
    let results: any[] = await Promise.all(
      needs.map(needs =>
        dispatch(
          fetchData({
            url: needs.url,
            dataBody: needs.data,
            withoutCancel: true,
          })
        )
      )
    );
    results = results.map((res: any) => res.payload?.data);

    listLogicalId.forEach((item: any, i: number) => {
      item.count = results?.[i]?.count ?? 0;
    });

    listLogicalId = sortByAttribute(listLogicalId, 'name', true);

    return {
      xaxis: {
        categories: listLogicalId.map(f => f.name),
      },
      series: [
        {
          name: 'Type',
          data: listLogicalId.map(f => f.count),
        },
      ],
    };
  };
  const getDataForChartItemLabels = async () => {
    let listLabels: any[] = [];
    let reqListLabels = {
      url: '/api/tags',
      data: {
        filters: { location: { isAlive: true } },
        findAndCount: true,
      },
    };
    const data = (await dispatch(fetchData({ url: reqListLabels.url, dataBody: reqListLabels.data, withoutCancel: true })))
      .payload?.data;

    if (data?.rows) {
      listLabels = getDistinct(data.rows, ['logicalId']);
    }

    let needs: any[] = [];

    listLabels.forEach(item => {
      needs.push({
        url: '/api/v2/items',
        data: {
          filters: { isAlive: true, tag: { name: [item.name] } },
          findAndCount: true,
          limit: 1,
          offset: 0,
        },
      });
    });
    let results: any[] = await Promise.all(
      needs.map(needs =>
        dispatch(
          fetchData({
            url: needs.url,
            dataBody: needs.data,
            withoutCancel: true,
          })
        )
      )
    );
    results = results.map((res: any) => res.payload?.data);
    listLabels.forEach((item: any, i: number) => {
      item.count = results?.[i]?.count ?? 0;
    });

    listLabels = sortByAttribute(listLabels, 'name');

    listLabels = sortByAttribute(listLabels, 'name', true);
    return {
      xaxis: {
        categories: listLabels.map(l => l.name),
      },
      series: [
        {
          name: 'Label',
          data: listLabels.map(l => l.count),
        },
      ],
    };
  };
  //AREACHART
  const getDataForChartEventsTime = async (type: string) => {
    let listEvents: any[] = [];
    const rangeDates = generateDateArray(new Date(), type);

    let reqListDates: any[] = [];

    for (let index = 0; index < rangeDates.length; index++) {
      const startDate = rangeDates[index];
      const endDate = rangeDates[index + 1] ?? new Date();

      const dayOfWeek = startDate.toLocaleDateString(lng, { weekday: 'long' });
      const month = startDate.toLocaleDateString(lng, { month: 'long' });

      listEvents.push({
        label: type === 'week' ? dayOfWeek : month,
        series: [],
      });
      reqListDates.push({
        url: '/api/v2/events',
        data: {
          where: {
            timestamp: {
              $gte: startDate.toISOString(),
              $lte: endDate.toISOString(),
            },
          },
          findAndCount: true,
        },
      });
    }

    let results = await Promise.all(
      reqListDates.map(needs =>
        dispatch(
          fetchData({
            key: 'events',
            url: needs.url,
            dataBody: needs.data,
            withoutCancel: true,
          })
        )
      )
    );
    results = results.map((res: any) => res.payload?.data);
    const values = [...results];

    const groupEventCounts = (listEventCount: any[]): any[] => {
      return listEventCount.reduce((result, obj) => {
        const existingObj = result.find((item: any) => item.name === obj.name);

        if (existingObj) {
          existingObj.data.push(obj.count);
        } else {
          result.push({
            name: obj.name,
            count: obj.count,
          });
        }

        return result;
      }, []);
    };

    values.forEach((item: any, i: number) => {
      if (item?.rows) {
        let listEventCount = getDistinct(item.rows, ['eventTypeId']);
        listEvents[i].series = groupEventCounts(listEventCount);
      }
    });

    const disctinctEventTypes: any[] = [];

    listEvents.forEach(event => {
      event.series.forEach((type: any) => {
        if (!disctinctEventTypes.includes(type.name)) {
          disctinctEventTypes.push(type.name);
        }
      });
    });

    const orderSeries = sortByAttribute(
      disctinctEventTypes.map(type => {
        return {
          name: type,
          data: listEvents.map(l => {
            const serie = l.series.find((e: any) => e.name === type);
            return serie ? serie.count : 0;
          }),
        };
      }),
      'name',
      true
    );

    return {
      xaxis: {
        categories: listEvents.map(l => l.label),
      },
      series: orderSeries.length === 0 ? generateArrayEmpty(type) : orderSeries,
    };
  };
  const getDataForChartReadingsTime = async (type: string) => {
    let listReadings: any[] = [];
    const rangeDates = generateDateArray(new Date(), type);

    let reqListDates: any[] = [];

    for (let index = 0; index < rangeDates.length; index++) {
      const startDate = rangeDates[index];
      const endDate = rangeDates[index + 1] ?? new Date();

      const dayOfWeek = startDate.toLocaleDateString(lng, { weekday: 'long' });
      const month = startDate.toLocaleDateString(lng, { month: 'long' });

      listReadings.push({
        label: type === 'week' ? dayOfWeek : month,
        series: [],
      });
      reqListDates.push({
        url: '/api/v2/readings',
        data: {
          where: {
            timestamp: {
              $gte: startDate.toISOString(),
              $lte: endDate.toISOString(),
            },
          },
          findAndCount: true,
          limit: 0,
          offset: 0,
        },
      });
    }

    let results = await Promise.all(
      reqListDates.map(needs =>
        dispatch(
          fetchData({
            key: 'readings',
            url: needs.url,
            dataBody: needs.data,
            withoutCancel: true,
          })
        )
      )
    );
    results = results.map((res: any) => res.payload?.data);
    const values = [...results];

    const groupReadingCounts = (listEventCount: any[]): any[] => {
      return listEventCount.reduce((result, obj) => {
        const existingObj = result.find((item: any) => item.name === obj.name);

        if (existingObj) {
          existingObj.data.push(obj.count);
        } else {
          result.push({
            name: obj.name,
            count: obj.count,
          });
        }

        return result;
      }, []);
    };

    values.forEach((item: any, i: number) => {
      // return item no {count, rows}
      if (item) {
        let listReadingCount = getDistinct(item, ['readingTypeId']);
        listReadingCount = sortByAttribute(listReadingCount, 'name');
        listReadings[i].series = groupReadingCounts(listReadingCount);
      }
    });

    const disctinctEventTypes: any[] = [];

    listReadings.forEach(event => {
      event.series.forEach((type: any) => {
        if (!disctinctEventTypes.includes(type.name)) {
          disctinctEventTypes.push(type.name);
        }
      });
    });

    const orderSeries = sortByAttribute(
      disctinctEventTypes.map(type => {
        return {
          name: type,
          data: listReadings.map(l => {
            const serie = l.series.find((e: any) => e.name === type);
            return serie ? serie.count : 0;
          }),
        };
      }),
      'name',
      true
    );

    return {
      xaxis: {
        categories: listReadings.map(l => l.label),
      },
      series: orderSeries.length === 0 ? generateArrayEmpty(type) : orderSeries,
    };
  };
  //PIECHART
  /*   const getDataForChartWarningBySeverity = async () => {
    return {
      labels: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
      series: [10, 3, 30, 2, 3, 4, 8, 0, 2, 0, 2, 5, 5, 3, 2],
    };
  }; */

  //#endregion

  //#region Renders
  const renderListKPICards = (listKPICards: any[]) => {
    return listKPICards.map(item => (
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }} key={item.id}>
        <KPICard title={item.title} loading={item.loading} dataCard={item.data} />
      </Grid>
    ));
  };
  const renderListAreaCharts = (listAreaCharts: any[]) => {
    return listAreaCharts.map(item => (
      <Grid size={{ xs: 12, md: 6, lg: 6 }} key={item.id} sx={{ height: '500px' }}>
        <AreaChart
          {...props}
          id={item.id}
          title={item.title}
          loading={item.loading}
          dataChart={item.data}
          onChangeSlot={v => onChangeSlot(item, v)}
        />
      </Grid>
    ));
  };
  const renderListBarCharts = (listBarCharts: any[]) => {
    const listNoEmptyData = listBarCharts.filter(item => !checkEmptyData(item.data));

    const gridSize = (value: number) => {
      if (value === 1) {
        return 12;
      } else if (value === 2) {
        return 6;
      } else if (value === 3) {
        return 4;
      } else {
        return 4;
      }
    };

    if (listNoEmptyData.length > 0) {
      return (
        listNoEmptyData.length > 0 &&
        listNoEmptyData.map(item => {
          return (
            <Grid size={{ xs: 12, md: 5, lg: gridSize(listNoEmptyData.length) }} key={item.id}>
              <BarChart {...props} options={{ title: item.title }} loading={item.loading} dataChart={item.data} />
            </Grid>
          );
        })
      );
    } else {
      return listBarCharts.map(item => {
        return (
          <Grid size={{ xs: 12, md: 5, lg: gridSize(listNoEmptyData.length) }} key={item.id}>
            <BarChart {...props} options={{ title: item.title }} loading={item.loading} dataChart={item.data} />
          </Grid>
        );
      });
    }
  };

  /* const renderListPieCharts = (listPieCharts: any[]) => {
    return listPieCharts.map((item, i) => (
      <Grid item xs={12} md={5} lg={4} style={{ height: '100%' }} key={i}>
        <KPI.PieChart {...props} title={item.title} loading={item.loading} dataChart={item.data} />
      </Grid>
    ));
  }; */
  //#endregion

  useEffect(() => {
    updateData(renderedCards, setRenderedCards);
    //updateData(renderedPieCharts, setRenderedPieCharts);
    updateData(renderedBarCharts, setRenderedBarCharts);
    //updateData(renderedAreaCharts, setRenderedAreaCharts);
  }, []);

  useEffect(() => {
    /* if (slot.id !== undefined) {
      const test = [...renderedAreaCharts];
      test[slot.id].loading = true;
      setRenderedAreaCharts(test);
    } */
    updateData(renderedAreaCharts, setRenderedAreaCharts, slot.id);
  }, [slot]);

  return (
    <Grid container size={12} rowSpacing={4.5} columnSpacing={2.75} p={4}>
      {/* row 1 */}
      {/* <Grid item xs={12} sx={{ mb: -2.25 }}>
        <Typography variant="h5">Dashboard</Typography>
      </Grid> */}
      {renderListKPICards(renderedCards)}

      {/* row 2 */}
      {renderListAreaCharts(renderedAreaCharts)}

      {/* row 3 */}
      {/* {renderListPieCharts(renderedPieCharts)} */}
      {renderListBarCharts(renderedBarCharts)}

      <GridBreak />

      {/* <Grid item xs={12} md={5} lg={4} style={{ height: '100%' }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h5">Warnings by severity</Typography>
          </Grid>
          <Grid item />
        </Grid>
        <KPI.MainCard sx={{ mt: 2, height: '90%' }} content={false}>
          <KPI.PieChart {...props} />
        </KPI.MainCard>
      </Grid> */}

      {/* row 3 */}
      {/* <Grid container item rowSpacing={4.5} columnSpacing={2.75} style={{ height: '620px' }}>
        <Grid item xs={12} md={7} lg={8} style={{ height: '100%' }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h5">Closed Loop Logistics - RTI inventory per location</Typography>
            </Grid>
            <Grid item />
          </Grid>
          <KPI.MainCard sx={{ mt: 2, height: '90%', pt: 2 }} content={false}>
            <KPI.StackedBarChart />
          </KPI.MainCard>
        </Grid>
        <Grid item xs={12} md={5} lg={4}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h5">Assets / Devices</Typography>
            </Grid>
            <Grid item />
          </Grid>
           <KPI.MainCard sx={{ mt: 2 }} content={false}>
            <BarChart {...props} type={1} color={firstColor} />
          </KPI.MainCard> 
        </Grid>
      </Grid> */}

      {/* row 4 */}
      {/*   <Grid container item rowSpacing={4.5} columnSpacing={2.75} style={{ height: '620px' }}>
        <Grid item xs={12} md={7} lg={8} style={{ height: '100%' }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h5">Tasks Execution</Typography>
            </Grid>
            <Grid item />
          </Grid>
          <KPI.MainCard sx={{ mt: 2, height: '90%' }} content={false}>
            <KPI.TasksTable />
          </KPI.MainCard>
        </Grid>
        <Grid item xs={12} md={5} lg={4} style={{ height: '100%' }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h5">Personnel on site</Typography>
            </Grid>
            <Grid item />
          </Grid>
           <KPI.MainCard sx={{ mt: 2, height: '90%' }} content={false}>
            <BarChart {...props} type={2} color="#0a8103" />
          </KPI.MainCard> 
        </Grid>
      </Grid> */}
    </Grid>
  );
};

export default DashboardPage;
